import { useMemo } from 'react';
import { doesUserProfileFieldMatchSearch } from './doesUserProfileFieldMatchSearch';
import { ClientDataType } from '../../types';
import { useGetFieldDisplayName } from '../../hooks/useGetFieldDisplayName';
import { useTranslation } from '../../hooks/useTranslation';
import { addPredictiveGoalsIcon } from '../addPredictiveGoalsIcon';
import { ES_FIELD_UNINDXED_DATA } from '../../constants';
import { useFieldMappingsContext } from '../../contexts/fieldMappings';

type UseUserProfileFieldOptionsProps = {
  search: string;
};

export const useUserProfileOptions = ({ search }: UseUserProfileFieldOptionsProps) => {
  const { t } = useTranslation();

  const { userEventMappings, didUserEventMappingsError, isUserEventMappingsLoading } =
    useFieldMappingsContext();

  const { getFieldDisplayName } = useGetFieldDisplayName();

  const options = useMemo(
    () =>
      userEventMappings
        .filter(
          ({ name, fieldType }) =>
            doesUserProfileFieldMatchSearch(
              getFieldDisplayName(name, ClientDataType.User),
              fieldType,
              search,
              // This removal is done for the userMappings endpoint on the BE, but for some reason not applied for all mappings endpoint
            ) && name !== ES_FIELD_UNINDXED_DATA,
        )
        .map(({ name, fieldType }) => ({
          badge: t(`fieldTypes.${fieldType}`),
          title: getFieldDisplayName(name, ClientDataType.User),
          value: name,
          ...addPredictiveGoalsIcon(name),
        }))
        .sort(({ title: a }, { title: b }) => a.localeCompare(b)),
    [userEventMappings, search, getFieldDisplayName, t],
  );

  return { options, didError: didUserEventMappingsError, isLoading: isUserEventMappingsLoading };
};
